import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import api from '../lib/api'
import Button from '../components/ui/Button'
import { Card, CardHeader } from '../components/ui/Card'
import BlocklyWorkspace from '../components/studio/BlocklyWorkspace'
import PythonRunner from '../components/studio/PythonRunner'
import StudyResources from '../components/studio/StudyResources'
import { generateLocalModules, getBnccForGrade, getFallbackCatalog, getFallbackModule, getGradeTopics } from '../data/bnccContent'

const dimensions = ['regular', 'adapted', 'blocks', 'games']
const dimensionLabels = {
  regular: 'Matemática regular com editor',
  adapted: 'Ensino adaptado com ABA e material dourado',
  blocks: 'Programação em blocos sobre o mesmo conteúdo',
  games: 'Games em Python sobre o mesmo conteúdo'
}

function emptyEditors() {
  return { regular: {}, adapted: {}, blocks: {}, games: {} }
}

function mergeCatalog(apiModules, grade) {
  const fallback = getFallbackCatalog(grade)
  const merged = [...(apiModules || [])]
  const seen = new Set(merged.map((item) => `${item.grade}-${item.topic}-${item.dimension}`))
  fallback.forEach((item) => {
    const key = `${item.grade}-${item.topic}-${item.dimension}`
    if (!seen.has(key)) merged.push(item)
  })
  return merged.sort((a, b) => String(a.topic).localeCompare(String(b.topic)) || dimensions.indexOf(a.dimension) - dimensions.indexOf(b.dimension))
}

function pythonProfileHint(profile = 'geral') {
  const map = {
    TEA: 'Use uma missão previsível, com uma instrução por vez e feedback visual claro.',
    TDAH: 'Use fases curtas, meta visível e feedback imediato a cada tentativa.',
    TOC: 'Use início e fim explícitos, limite de conferências e mensagem de conclusão.',
    Down: 'Use linguagem simples, repetição positiva e números menores antes da generalização.',
    geral: 'Use instruções curtas, feedback imediato e permita novas tentativas.'
  }
  return map[profile] || map.geral
}

function defaultPythonCode({ topic, grade, profile = 'geral', exerciseNumber = 1, prompt = '', skillReference = '' }) {
  const base = Math.max(2, Number(grade || 1) + Number(exerciseNumber || 1))
  const expected = base * 2
  return `# ${grade}º ano • ${topic} • ${skillReference || 'BNCC'}
# Perfil: ${profile}
# ${pythonProfileHint(profile)}

score = 0
resposta_esperada = ${expected}
print("Missão matemática: ${String(prompt || topic).replace(/\"/g, '\\"')}")
print("Resolva o desafio e altere resposta_aluno para testar.")

resposta_aluno = ${base} + ${base}

if resposta_aluno == resposta_esperada:
    score += 10
    print("✅ Acertou! Pontos:", score)
else:
    print("🔁 Tente novamente. Confira a estratégia usada.")

print("Resposta esperada:", resposta_esperada)`
}


export default function LearningStudioPage({ user }) {
  const [searchParams] = useSearchParams()
  const [catalog, setCatalog] = useState([])
  const [bncc, setBncc] = useState([])
  const [selectedGrade, setSelectedGrade] = useState(1)
  const [selectedDimension, setSelectedDimension] = useState('regular')
  const [selectedSupport, setSelectedSupport] = useState('geral')
  const [selectedTopic, setSelectedTopic] = useState(getGradeTopics(1)[0]?.title || '')
  const [loading, setLoading] = useState(true)
  const [message, setMessage] = useState('')
  const [exerciseCount, setExerciseCount] = useState(10)
  const [generating, setGenerating] = useState(false)
  const [editors, setEditors] = useState(emptyEditors())
  const [localModulesMap, setLocalModulesMap] = useState({})
  const [moduleDataMap, setModuleDataMap] = useState({})
  const [teacherNotes, setTeacherNotes] = useState('')
  const [teacherHistory, setTeacherHistory] = useState([])
  const [teacherPackages, setTeacherPackages] = useState([])
  const [publishing, setPublishing] = useState(false)

  useEffect(() => {
    const gradeParam = Number(searchParams.get('grade') || 0)
    const topicParam = searchParams.get('topic') || ''
    const dimensionParam = searchParams.get('dimension') || ''
    const supportParam = searchParams.get('support') || ''
    if (gradeParam >= 1 && gradeParam <= 9) setSelectedGrade(gradeParam)
    if (dimensions.includes(dimensionParam)) setSelectedDimension(dimensionParam)
    if (supportParam) setSelectedSupport(supportParam)
    if (topicParam) setSelectedTopic(topicParam)
  }, [searchParams])

  useEffect(() => { fetchCatalog() }, [selectedGrade])
  useEffect(() => { if (user) { fetchTeacherHistory(); fetchTeacherPackages() } }, [user, selectedGrade])

  useEffect(() => {
    const availableTopics = getGradeTopics(selectedGrade)
    if (!availableTopics.find((item) => item.title === selectedTopic)) {
      setSelectedTopic(availableTopics[0]?.title || '')
    }
  }, [selectedGrade, selectedTopic])

  useEffect(() => {
    if (!catalog.length || !selectedTopic) return
    loadTopicModules()
  }, [catalog, selectedGrade, selectedTopic, selectedSupport])

  const topics = useMemo(() => getGradeTopics(selectedGrade).map((item) => item.title), [selectedGrade])
  const currentBncc = useMemo(() => {
    const apiBncc = (bncc || []).filter((item) => Number(item.grade) === Number(selectedGrade) && (!selectedTopic || item.title === selectedTopic))
    const fallback = getBnccForGrade(selectedGrade).filter((item) => !selectedTopic || item.title === selectedTopic)
    return apiBncc.length ? apiBncc : fallback
  }, [bncc, selectedGrade, selectedTopic])
  const orderedModules = useMemo(() => dimensions.map((dimension) => moduleDataMap[dimension]).filter(Boolean), [moduleDataMap])

  const fetchCatalog = async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/learning/catalog', { params: { grade: selectedGrade } })
      setCatalog(mergeCatalog(data.modules || [], selectedGrade))
      setBncc((data.bncc || []).length ? data.bncc : getBnccForGrade(selectedGrade))
      setMessage('')
    } catch {
      setCatalog(getFallbackCatalog(selectedGrade))
      setBncc(getBnccForGrade(selectedGrade))
      setMessage('Conteúdo carregado em modo local BNCC enquanto a API não responde.')
    } finally {
      setLoading(false)
    }
  }



  const fetchTeacherPackages = async () => {
    if (!user) return
    try {
      const { data } = await api.get('/learning/teacher-packages', { params: { grade: selectedGrade } })
      setTeacherPackages(data.packages || [])
    } catch {
      setTeacherPackages([])
    }
  }

  const fetchTeacherHistory = async () => {
    try {
      const { data } = await api.get('/learning/teacher-requests')
      setTeacherHistory(data.requests || [])
    } catch {
      setTeacherHistory([])
    }
  }

  const fetchSingleModule = async (module) => {
    if (!module) return null
    if (String(module.id).startsWith('fallback-')) return getFallbackModule(module.id, Number(module.exerciseCount) || 10, selectedSupport)
    if (localModulesMap[module.id]) return localModulesMap[module.id]
    try {
      const { data } = await api.get(`/learning/module/${module.id}`)
      return data
    } catch {
      const fallbackMatch = getFallbackCatalog(selectedGrade).find((item) => item.topic === module.topic && item.dimension === module.dimension)
      return fallbackMatch ? getFallbackModule(fallbackMatch.id, Number(module.exerciseCount) || 10, selectedSupport) : null
    }
  }

  const loadTopicModules = async () => {
    const topicModules = dimensions
      .map((dimension) => catalog.find((item) => item.grade === selectedGrade && item.topic === selectedTopic && item.dimension === dimension))
      .filter(Boolean)
    const results = await Promise.all(topicModules.map(async (item) => ({ dimension: item.dimension, data: await fetchSingleModule(item) })))
    const nextMap = {}
    results.forEach(({ dimension, data }) => { if (data) nextMap[dimension] = data })
    setModuleDataMap(nextMap)
  }

  const handleGenerate = async () => {
    try {
      setGenerating(true)
      setMessage('')
      const requested = Math.max(10, Math.min(100, Math.ceil(Number(exerciseCount || 10) / 10) * 10))
      if (user) {
        try {
          const { data } = await api.post('/learning/generate', {
            grade: selectedGrade,
            topic: selectedTopic,
            requestedCount: requested,
            notes: 'Solicitação feita pelo professor/responsável no estúdio.',
            supportProfile: selectedSupport,
            skillReference: currentBncc[0]?.skillReference || ''
          })
          setMessage(`Os quatro módulos do mesmo conteúdo foram gerados. Custo em créditos: ${data.creditCost}.`)
          await fetchCatalog()
          if (data.modules?.length) {
            const fetched = await Promise.all(data.modules.map(async (item) => ({ dimension: item.dimension, data: await fetchSingleModule(item) })))
            const nextMap = {}
            fetched.forEach(({ dimension, data }) => { if (data) nextMap[dimension] = data })
            setModuleDataMap(nextMap)
            return
          }
        } catch {
          // fallback local logo abaixo
        }
      }
      const generated = generateLocalModules({ grade: selectedGrade, topic: selectedTopic, requestedCount: requested, supportProfile: selectedSupport })
      setCatalog((current) => mergeCatalog([
        ...current.filter((item) => !(item.grade === selectedGrade && item.topic === selectedTopic)),
        ...generated.modules
      ], selectedGrade))
      setLocalModulesMap((current) => ({ ...current, ...generated.moduleMap }))
      const nextMap = {}
      dimensions.forEach((dimension) => {
        const module = generated.modules.find((item) => item.dimension === dimension)
        if (module) nextMap[dimension] = generated.moduleMap[module.id]
      })
      setModuleDataMap(nextMap)
      setMessage(user ? 'API indisponível: os quatro módulos foram gerados localmente e estão prontos para uso.' : 'Exercícios gerados localmente. Os quatro módulos do mesmo conteúdo já estão abertos.')
    } finally {
      setGenerating(false)
    }
  }



  const handleTeacherPublish = async () => {
    try {
      setPublishing(true)
      const requested = Math.max(10, Math.min(100, Math.ceil(Number(exerciseCount || 10) / 10) * 10))
      if (user) {
        const { data } = await api.post('/learning/teacher-requests/generate-series', {
          grade: selectedGrade,
          topic: selectedTopic,
          requestedCount: requested,
          notes: teacherNotes || `Professor/tutor publicou um pacote para ${selectedGrade}º ano.`,
          supportProfile: selectedSupport,
          skillReference: currentBncc[0]?.skillReference || ''
        })
        setMessage(data.message || 'Pacote da série publicado no estúdio.')
        setTeacherHistory(data.requests || [])
        await fetchCatalog()
        await fetchTeacherPackages()
        if (data.modules?.length) {
          const fetched = await Promise.all(data.modules.map(async (item) => ({ dimension: item.dimension, data: await fetchSingleModule(item) })))
          const nextMap = {}
          fetched.forEach(({ dimension, data }) => { if (data) nextMap[dimension] = data })
          setModuleDataMap(nextMap)
        }
        return
      }

      const generated = generateLocalModules({ grade: selectedGrade, topic: selectedTopic, requestedCount: requested, supportProfile: selectedSupport })
      setCatalog((current) => mergeCatalog([
        ...current.filter((item) => !(item.grade === selectedGrade && item.topic === selectedTopic)),
        ...generated.modules
      ], selectedGrade))
      setLocalModulesMap((current) => ({ ...current, ...generated.moduleMap }))
      setTeacherHistory((current) => [{
        id: `local-${Date.now()}`,
        grade: selectedGrade,
        topic: selectedTopic,
        requestedCount: requested,
        notes: teacherNotes || 'Publicado localmente',
        status: 'generated',
        createdAt: new Date().toISOString()
      }, ...current].slice(0, 10))
      setMessage('Pacote da série publicado localmente no estúdio.')
    } finally {
      setPublishing(false)
    }
  }

  const updateEditor = (dimension, exerciseId, value) => {
    setEditors((current) => ({ ...current, [dimension]: { ...current[dimension], [exerciseId]: value } }))
  }

  return (
    <div className="page-stack">
      <section className="section-title-wrap">
        <span className="eyebrow">BNCC + 4 dimensões</span>
        <h1>Estúdio de aprendizagem por série, habilidade e editor.</h1>
        <p>O mesmo conteúdo escolhido é aberto junto em matemática regular, ensino adaptado, programação em blocos e games em Python.</p>
      </section>

      {message ? <div className="alert info">{message}</div> : null}

      <div className="grid cols-4">
        <Card>
          <label className="form-label">Série</label>
          <select className="input" value={selectedGrade} onChange={(e) => setSelectedGrade(Number(e.target.value))}>
            {Array.from({ length: 9 }, (_, index) => index + 1).map((grade) => <option key={grade} value={grade}>{grade}º ano</option>)}
          </select>
        </Card>
        <Card>
          <label className="form-label">Conteúdo BNCC da série</label>
          <select className="input" value={selectedTopic} onChange={(e) => setSelectedTopic(e.target.value)}>
            {topics.map((topic) => <option key={topic} value={topic}>{topic}</option>)}
          </select>
        </Card>
        <Card>
          <label className="form-label">Dimensão em destaque</label>
          <select className="input" value={selectedDimension} onChange={(e) => setSelectedDimension(e.target.value)}>
            <option value="regular">Ensino regular</option>
            <option value="adapted">Ensino adaptado</option>
            <option value="blocks">Programação em blocos</option>
            <option value="games">Games em Python</option>
          </select>
        </Card>
        <Card>
          <label className="form-label">Perfil de suporte</label>
          <select className="input" value={selectedSupport} onChange={(e) => setSelectedSupport(e.target.value)}>
            <option value="geral">Geral</option>
            <option value="TEA">TEA</option>
            <option value="TDAH">TDAH</option>
            <option value="TOC">TOC</option>
            <option value="Down">Síndrome de Down</option>
            <option value="outros">Outros perfis</option>
          </select>
        </Card>
        <Card>
          <label className="form-label">Criar mais 10 exercícios</label>
          <div className="button-wrap">
            <input className="input" type="number" min="10" max="100" step="10" value={exerciseCount} onChange={(e) => setExerciseCount(Math.max(10, Math.min(100, Math.ceil(Number(e.target.value || 10) / 10) * 10)))} />
            <Button onClick={handleGenerate} disabled={generating || !selectedTopic}>{generating ? 'Gerando...' : 'Criar mais 10 exercícios'}</Button>
          </div>
        </Card>
      </div>

      <Card>
        <CardHeader title="Professor/tutor" subtitle="Gerar, publicar e acompanhar pacotes por série" />
        <div className="grid cols-2">
          <div className="stack-sm">
            <p>Ao publicar, o sistema amplia o pacote em blocos de 10 atividades por vez para a série e assunto BNCC escolhidos, mantendo o histórico do professor.</p>
            <textarea className="input textarea" placeholder="Notas do professor/tutor para esta série e conteúdo" value={teacherNotes} onChange={(e) => setTeacherNotes(e.target.value)} />
            <div className="button-wrap">
              <Button onClick={handleTeacherPublish} disabled={publishing || !selectedTopic}>{publishing ? 'Publicando...' : 'Professor: criar +10 e publicar'}</Button>
            </div>
          </div>
          <div className="stack-sm">
            <div className="surface-card compact">
              <strong>Pacotes persistidos por professor/série</strong>
              {(teacherPackages || []).filter((item) => item.grade === selectedGrade).slice(0, 6).map((item) => (
                <div key={`pkg-${item.id}`} className="support-row">
                  <strong>{item.grade}º ano • {item.topic}</strong>
                  <p>{item.requestedCount} exercícios persistidos • status {item.status}</p>
                  {item.notes ? <p>{item.notes}</p> : null}
                </div>
              ))}
              {!teacherPackages.filter((item) => item.grade === selectedGrade).length ? <p>Nenhum pacote persistido nesta série ainda.</p> : null}
            </div>
            {(teacherHistory || []).slice(0, 6).map((item) => (
              <div key={item.id} className="surface-card compact">
                <strong>{item.grade}º ano • {item.topic}</strong>
                <p>Status: {item.status} • {item.requestedCount} exercícios</p>
                {item.notes ? <p>{item.notes}</p> : null}
              </div>
            ))}
            {!teacherHistory.length ? <p>Nenhuma geração recente ainda.</p> : null}
          </div>
        </div>
      </Card>

      <StudyResources grade={selectedGrade} topic={selectedTopic} />

      <div className="grid cols-2">
        <Card>
          <CardHeader title="Base BNCC por série" subtitle="Competências e habilidades que guiam o módulo" />
          <div className="stack-sm">
            {currentBncc.map((skill) => (
              <div key={`${skill.skillReference}-${skill.title || skill.id}`} className="support-row">
                <strong>{skill.title || skill.unitTheme}</strong>
                <p><strong>Unidade temática:</strong> {skill.unitTheme}</p>
                <p><strong>Competência:</strong> {skill.competenceTitle}</p>
                <p><strong>Habilidade:</strong> {skill.skillTitle}</p>
                <p><strong>Referência:</strong> {skill.skillReference}</p>
                <p><strong>Objetivos:</strong> {skill.learningObjectives}</p>
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <CardHeader title="Métodos alternativos" subtitle="Outras matemáticas quando fizer sentido" />
          <ul className="list-soft">
            <li>Singapura: barras, comparação, decomposição e visualização estruturada.</li>
            <li>Japão: representação visual, discussão coletiva e múltiplos caminhos.</li>
            <li>Finlândia: autonomia, justificativa, contexto e resolução aberta.</li>
            <li>China: treino de fluência com generalização de padrões e variações.</li>
          </ul>
        </Card>
      </div>

      {loading ? <div className="loading-box">Carregando módulos...</div> : null}

      {!loading && orderedModules.length ? orderedModules.map(({ module, exercises }) => (
        <Card key={module.id}>
          <CardHeader title={dimensionLabels[module.dimension] || module.title} subtitle={`${module.title} • ${module.exerciseCount || exercises.length} exercícios`} />
          <p>{module.summary}</p>
          <p><strong>Orientação docente:</strong> {module.teacherNotes}</p>
          {module.dimension === selectedDimension ? <div className="alert info">Dimensão em destaque.</div> : null}
          {(module.dimension === 'blocks' || module.dimension === 'games') ? <div className="alert info">Atividade sincronizada com {selectedGrade}º ano • {selectedTopic} • perfil {selectedSupport}.</div> : null}
          <div className="stack-sm">
            {exercises.map((exercise) => (
              <div key={exercise.id} className="surface-card compact">
                <div className="section-head">
                  <div>
                    <h3>{exercise.title}</h3>
                    <p>{exercise.prompt}</p>
                  </div>
                  <span className="badge badge-info">#{exercise.exerciseNumber}</span>
                </div>
                <p><strong>Estratégia de apoio:</strong> {exercise.supportStrategy}</p>
                <p><strong>Solução regular:</strong> {exercise.regularSolution}</p>
                <p><strong>Solução alternativa:</strong> {exercise.alternativeSolution}</p>
                {module.dimension === 'blocks' ? <p><strong>Sequência em blocos:</strong> {exercise.blocksSolution}</p> : null}
                {module.dimension === 'games' ? <p><strong>Desafio no game:</strong> {exercise.gameChallenge}</p> : null}

                {module.dimension === 'regular' ? (
                  <textarea className="input textarea" placeholder="Professor/aluno pode escrever a solução aqui." value={editors.regular[exercise.id] || ''} onChange={(e) => updateEditor('regular', exercise.id, e.target.value)} />
                ) : null}

                {module.dimension === 'adapted' ? (
                  <div className="grid cols-2">
                    <textarea className="input textarea" placeholder="Plano ABA: antecedente, prompt, resposta, reforço." value={editors.adapted[exercise.id] || ''} onChange={(e) => updateEditor('adapted', exercise.id, e.target.value)} />
                    <textarea className="input textarea" placeholder="Materiais adaptados: material dourado, cartões, pistas visuais, tempo, pausas." />
                  </div>
                ) : null}

                {module.dimension === 'blocks' ? (
                  <div className="grid cols-2">
                    <div>
                      <BlocklyWorkspace key={`${selectedGrade}-${selectedTopic}-${selectedSupport}-${exercise.id}`} value={editors.blocks[exercise.id] || ''} onChange={(value) => updateEditor('blocks', exercise.id, value)} context={{ grade: selectedGrade, topic: selectedTopic, profile: selectedSupport, skillReference: module.skillReference || currentBncc[0]?.skillReference || '', exerciseId: exercise.id, prompt: exercise.prompt, seedAnswer: exercise.exerciseNumber }} />
                    </div>
                    <div className="stack-sm">
                      <textarea className="input textarea" placeholder="O código JavaScript gerado a partir dos blocos aparece aqui." value={editors.blocks[exercise.id] || ''} onChange={(e) => updateEditor('blocks', exercise.id, e.target.value)} />
                      <textarea className="input textarea" value={exercise.starterCode || ''} readOnly />
                    </div>
                  </div>
                ) : null}

                {module.dimension === 'games' ? (
                  <div className="grid cols-2">
                    <textarea className="input textarea" value={editors.games[exercise.id] || defaultPythonCode({ topic: module.topic, grade: selectedGrade, profile: selectedSupport, exerciseNumber: exercise.exerciseNumber, prompt: exercise.prompt, skillReference: module.skillReference || currentBncc[0]?.skillReference || '' })} onChange={(e) => updateEditor('games', exercise.id, e.target.value)} />
                    <PythonRunner code={editors.games[exercise.id] || defaultPythonCode({ topic: module.topic, grade: selectedGrade, profile: selectedSupport, exerciseNumber: exercise.exerciseNumber, prompt: exercise.prompt, skillReference: module.skillReference || currentBncc[0]?.skillReference || '' })} />
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </Card>
      )) : null}
    </div>
  )
}
